import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { schemeImage } from '../../lib/site';
import { formatRent, type Listing } from '../../pages/site/ListingCard';

export interface PublicProperty {
  id: string; name: string; slug: string; region: string; city: string | null; postcode: string | null;
  lifecycleStage: string; description: string | null; amenities: string[]; unitCount: number; availableUnits?: number;
}

export interface Scheme extends PublicProperty {
  fromMinor: number | null;
  currency: string;
  rentPeriod: string;
  available: number;
  comingSoon: boolean;
}

/**
 * The "Explore Properties" grid on lotusliving.co.uk lists each scheme with a
 * "From £x pcm" price. We build the same view from live data: published listings are
 * grouped by scheme for the price and count, and schemes still in construction or
 * planning appear as "Coming soon".
 */
export function useSchemes() {
  return useQuery({
    queryKey: ['public-schemes'],
    queryFn: async (): Promise<Scheme[]> => {
      const [props, listings] = await Promise.all([
        api.public.get<PublicProperty[]>('/public/properties', { perPage: 100, sort: 'name' }),
        api.public.get<Listing[]>('/public/listings', { perPage: 200, sort: 'rent' }),
      ]);
      const bySlug = new Map<string, Listing[]>();
      for (const l of listings.data) {
        const key = l.property?.slug ?? '';
        bySlug.set(key, [...(bySlug.get(key) ?? []), l]);
      }
      return props.data
        .map((p) => {
          const ls = bySlug.get(p.slug) ?? [];
          const cheapest = ls.reduce<Listing | null>((m, l) => (!m || l.advertisedRentMinor < m.advertisedRentMinor ? l : m), null);
          const comingSoon = !['STABILISED', 'LEASE_UP'].includes(p.lifecycleStage);
          return {
            ...p,
            fromMinor: cheapest?.advertisedRentMinor ?? null,
            currency: cheapest?.advertisedRentCurrency ?? (p.region === 'AE' ? 'AED' : 'GBP'),
            rentPeriod: cheapest?.rentPeriod ?? 'MONTH',
            available: ls.length,
            comingSoon,
          };
        })
        .filter((s) => s.lifecycleStage !== 'DISPOSED')
        .sort((a, b) => Number(a.comingSoon) - Number(b.comingSoon) || b.available - a.available || a.name.localeCompare(b.name));
    },
    staleTime: 60_000,
  });
}

export function SchemeCard({ scheme, index = 0 }: { scheme: Scheme; index?: number }) {
  const price = scheme.comingSoon || scheme.fromMinor === null
    ? 'Coming soon'
    : `From ${formatRent(scheme.fromMinor, scheme.currency)} ${scheme.rentPeriod === 'YEAR' ? 'pa' : 'pcm'}`;
  const to = scheme.comingSoon ? `/rent?scheme=${scheme.slug}&soon=1` : `/rent?scheme=${scheme.slug}`;
  return (
    <Link to={to} className="group block">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[10px] bg-ink-100">
        <img src={schemeImage(scheme.slug, index)} alt={scheme.name} loading="lazy" className={`h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04] ${scheme.comingSoon ? 'saturate-[0.85]' : ''}`} />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink-900/75 via-ink-900/20 to-transparent" />
        <div className="absolute left-4 top-4">
          <span className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${scheme.comingSoon ? 'bg-white/90 text-ink-700' : 'bg-bronze-400 text-ink-900'}`}>{price}</span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/80">{scheme.city}{scheme.region === 'AE' ? ' · Dubai' : ''}</div>
          <div className="display mt-0.5 text-[26px] leading-tight">{scheme.name}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[13px]">
        <span className="text-ink-500">{scheme.comingSoon ? `${scheme.unitCount} homes · register interest` : scheme.available ? `${scheme.available} home${scheme.available === 1 ? '' : 's'} available now` : 'Fully let · join the waiting list'}</span>
        <span className="font-medium text-ink-900 transition group-hover:text-bronze-600">View →</span>
      </div>
    </Link>
  );
}

export function SchemeGrid({ limit, className = '' }: { limit?: number; className?: string }) {
  const { data, isLoading } = useSchemes();
  const items = (data ?? []).slice(0, limit ?? undefined);
  return (
    <div className={`grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 ${className}`}>
      {isLoading && Array.from({ length: limit ?? 6 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-[10px] bg-ink-100" />)}
      {items.map((s, i) => <SchemeCard key={s.id} scheme={s} index={i} />)}
    </div>
  );
}
